// src/pages/finance-portal/tax-documents/TaxDocumentDetail.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, Card, CardContent, Chip,
    Alert, CircularProgress, Grid, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT: { bg: '#f1f5f9', color: '#64748b' },
    GENERATED: { bg: '#dbeafe', color: '#1e40af' },
    REVIEWED: { bg: '#fef3c7', color: '#92400e' },
    FILED: { bg: '#dcfce7', color: '#166534' },
    CORRECTED: { bg: '#fce7f3', color: '#9d174d' },
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const W2_BOXES = [
    { key: 'box1_wages', label: 'Box 1 - Wages, Tips, Other Compensation' },
    { key: 'box2_federal_tax', label: 'Box 2 - Federal Income Tax Withheld' },
    { key: 'box3_ss_wages', label: 'Box 3 - Social Security Wages' },
    { key: 'box4_ss_tax', label: 'Box 4 - Social Security Tax Withheld' },
    { key: 'box5_medicare_wages', label: 'Box 5 - Medicare Wages and Tips' },
    { key: 'box6_medicare_tax', label: 'Box 6 - Medicare Tax Withheld' },
    { key: 'box16_state_wages', label: 'Box 16 - State Wages, Tips, etc.' },
    { key: 'box17_state_tax', label: 'Box 17 - State Income Tax' },
];

const NEC_BOXES = [
    { key: 'box1_nonemployee_compensation', label: 'Box 1 - Nonemployee Compensation' },
];

export default function TaxDocumentDetail() {
    const navigate = useNavigate();
    const { documentId } = useParams();

    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Edit dialog
    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);

    const fetchDocument = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/api/finance/payroll/tax-documents/${documentId}`);
            if (res.data.success) {
                setDoc(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load tax document');
        } finally {
            setLoading(false);
        }
    }, [documentId]);

    useEffect(() => { fetchDocument(); }, [fetchDocument]);

    const openEdit = () => {
        if (!doc) return;
        const boxes = doc.document_type === 'W-2' ? W2_BOXES : NEC_BOXES;
        const formData = {};
        boxes.forEach(b => { formData[b.key] = doc[b.key] || ''; });
        setEditForm(formData);
        setEditOpen(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload = {};
            Object.keys(editForm).forEach(k => {
                payload[k] = parseFloat(editForm[k]) || 0;
            });
            await axiosInstance.put(`/api/finance/payroll/tax-documents/${documentId}`, payload);
            setEditOpen(false);
            setSuccess('Document updated');
            fetchDocument();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update document');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    if (error && !doc) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error" sx={{ borderRadius: '12px', fontFamily, mb: 2 }}>{error}</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
                    sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Back</Button>
            </Box>
        );
    }

    if (!doc) return null;

    const boxes = doc.document_type === 'W-2' ? W2_BOXES : NEC_BOXES;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/finance-portal/tax-documents')}
                        sx={{ fontFamily, textTransform: 'none', color: 'rgba(255,255,255,0.8)', mb: 1, '&:hover': { color: '#fff' } }}>
                        Back to Tax Documents
                    </Button>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <DescriptionIcon sx={{ fontSize: 28, color: '#fff' }} />
                            </Box>
                            <Box>
                                <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                                    {doc.document_type} — {doc.tax_year}
                                </Typography>
                                <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                    {doc.employee_name || doc.contractor_name || 'Unknown'}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip
                                label={getStatusLabel(doc.status)}
                                sx={{
                                    fontFamily, fontWeight: 700, fontSize: '0.8rem',
                                    bgcolor: (STATUS_COLORS[doc.status] || STATUS_COLORS.DRAFT).bg,
                                    color: (STATUS_COLORS[doc.status] || STATUS_COLORS.DRAFT).color,
                                }}
                            />
                            <Button
                                variant="contained"
                                startIcon={<EditIcon />}
                                onClick={openEdit}
                                sx={{
                                    fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                    bgcolor: 'white', color: ACCENT,
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                }}
                            >
                                Edit
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 900, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Box Data */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', mb: 2 }}>
                            {doc.document_type === 'W-2' ? 'W-2 Box Data' : '1099-NEC Box Data'}
                        </Typography>
                        <Grid container spacing={2}>
                            {boxes.map(b => (
                                <Grid item xs={12} sm={6} key={b.key}>
                                    <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#64748b', fontWeight: 600, mb: 0.5 }}>
                                            {b.label}
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 800, color: '#1e293b' }}>
                                            {formatCurrency(doc[b.key])}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>

                {/* Employee/Contractor Info */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mt: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>
                            {doc.document_type === 'W-2' ? 'Employee Information' : 'Contractor Information'}
                        </Typography>
                        <Grid container spacing={2}>
                            {[
                                { label: 'Name', value: doc.employee_name || doc.contractor_name },
                                { label: 'SSN (last 4)', value: doc.ssn_last4 ? `***-**-${doc.ssn_last4}` : '—' },
                                { label: 'EIN', value: doc.ein || '—' },
                                { label: 'Tax Year', value: doc.tax_year },
                            ].map((item, idx) => (
                                <Grid item xs={6} sm={3} key={idx}>
                                    <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>{item.label}</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>{item.value || '—'}</Typography>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>
            </Box>

            {/* Edit Dialog */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Edit {doc.document_type} Data</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {boxes.map(b => (
                            <Grid item xs={12} sm={6} key={b.key}>
                                <TextField
                                    size="small" fullWidth label={b.label} type="number"
                                    value={editForm[b.key] || ''}
                                    onChange={e => setEditForm(prev => ({ ...prev, [b.key]: e.target.value }))}
                                    InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                    InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEditOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
